import { changeMetaThemeColor, lockScroll, unlockScroll } from '@theme/utilities';

class SignalHeader extends HTMLElement {
  /** @type {AbortController | undefined} */
  abortController;

  /** @type {HTMLElement[]} */
  menuTriggers = [];

  /** @type {HTMLElement[]} */
  menuPanels = [];

  /** @type {Map<string, HTMLElement>} */
  panelsByKey = new Map();

  /** @type {HTMLElement | null} */
  mobileTrigger = null;

  /** @type {HTMLElement | null} */
  mobileMenu = null;

  /** @type {HTMLDetailsElement | null} */
  mobileDrawer = null;

  /** @type {HTMLDetailsElement[]} */
  mobileGroups = [];

  /** @type {HTMLElement | null} */
  modeToggle = null;

  /** @type {Element | null} */
  headerComponent = null;

  /** @type {Element | null} */
  bar = null;

  desktopMedia = window.matchMedia('(min-width: 1180px)');
  hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');

  /** @type {string | undefined} */
  activeMenuKey;

  /** @type {HTMLElement | undefined} */
  activeMenuTrigger;

  /** @type {number | undefined} */
  desktopMenuOpenTimer;

  /** @type {number | undefined} */
  desktopMenuCloseTimer;

  /** @type {number | undefined} */
  railSwitchTimer;

  /** @type {number | undefined} */
  pointerFrame;

  /** @type {(() => void) | undefined} */
  cancelMenuImageWarm;

  menuImagesWarmed = false;

  /**
   * Holds the detached preload images. A detached Image with a load in flight
   * can be collected before it finishes, which would silently waste the warm.
   * @type {HTMLImageElement[]}
   */
  warmedImages = [];

  pointerX = 0;
  pointerY = 0;

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.menuTriggers = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('[data-signal-menu-trigger]'),
    ]);
    this.menuPanels = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('[data-signal-menu-panel]'),
    ]);
    this.mobileTrigger = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-signal-mobile-trigger]')
    );
    this.mobileMenu = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-signal-mobile-menu]')
    );
    this.mobileDrawer = /** @type {HTMLDetailsElement | null} */ (
      this.querySelector('[data-signal-mobile-drawer]')
    );
    this.mobileGroups = /** @type {HTMLDetailsElement[]} */ ([
      ...this.querySelectorAll('[data-signal-mobile-group]'),
    ]);
    this.modeToggle = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-signal-mode-toggle]')
    );
    this.headerComponent = this.closest('header-component');
    this.bar = this.querySelector('.signal-header__bar');

    this.panelsByKey = new Map();
    this.menuPanels.forEach((panel) => {
      const key = panel.dataset.signalMenuPanel;
      if (key) this.panelsByKey.set(key, panel);
    });

    this.menuPanels.forEach((panel) => {
      panel.hidden = true;
    });
    this.menuTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    this.menuTriggers.forEach((trigger) => {
      const key = trigger.dataset.signalMenuTrigger;
      if (!key || !this.panelsByKey.has(key)) return;

      trigger.addEventListener(
        'click',
        () => this.toggleDesktopMenu(key, trigger),
        { signal }
      );

      trigger.addEventListener(
        'pointerenter',
        () => {
          if (!this.desktopMedia.matches || !this.hoverMedia.matches) return;

          this.cancelDesktopMenuClose();
          this.scheduleDesktopMenuOpen(key, trigger);
        },
        { signal }
      );

      trigger.addEventListener(
        'pointerleave',
        () => this.cancelDesktopMenuOpen(),
        { signal }
      );
    });

    /*
     * Closing is decided by where the pointer actually is, not by pointerleave
     * on the trigger, the bar or the panel.
     *
     * Those elements do not tile the space the pointer crosses on its way from a
     * nav link down into the panel: the link is shorter than the bar, the bar
     * stops above the header's divider, and the panel starts below a gap. Every
     * attempt to close those seams by matching edges left another one, because
     * two regions that merely touch have no tolerance for a border or a
     * fractional device pixel.
     *
     * So on each move we measure the active trigger and the open panel and
     * treat the corridor between them as part of the menu. The trigger's own
     * column, not the full header width, is what's forgiving vertically — the
     * bar is taller than the link — so moving sideways off the trigger, along
     * the bar toward another link or the header icons, still closes the menu.
     * Nothing here is hardcoded, so it survives changes to the bar height, the
     * link height and the gap.
     */
    document.addEventListener(
      'pointermove',
      (event) => {
        if (this.activeMenuKey === undefined) return;
        if (!this.desktopMedia.matches || !this.hoverMedia.matches) return;

        this.pointerX = event.clientX;
        this.pointerY = event.clientY;

        if (this.pointerFrame !== undefined) return;

        this.pointerFrame = requestAnimationFrame(() => {
          this.pointerFrame = undefined;

          if (this.isPointerInMenuRegion(this.pointerX, this.pointerY)) {
            this.cancelDesktopMenuClose();
          } else {
            this.scheduleDesktopMenuClose();
          }
        });
      },
      { signal }
    );

    // Pointer left the document entirely — no pointermove will follow.
    document.addEventListener(
      'pointerleave',
      () => this.scheduleDesktopMenuClose(),
      { signal }
    );

    /*
     * The <summary> already opens and closes its <details> with no script —
     * that is the whole point of C1. This only keeps aria-expanded, the
     * desktop menus, the accordions and the scroll lock in step with the
     * native open state, and it has to be a 'toggle' listener rather than a
     * 'click' on the trigger: a real toggle can also come from Escape/outside
     * -tap closing it programmatically below, and both paths need the same
     * sync.
     */
    this.mobileDrawer?.addEventListener(
      'toggle',
      () => this.syncMobileDrawer(),
      { signal }
    );

    this.modeToggle?.addEventListener(
      'click',
      () => this.toggleColorMode(),
      { signal }
    );

    this.syncColorMode();

    this.mobileGroups.forEach((group) => {
      group.addEventListener(
        'toggle',
        () => {
          if (!group.open) return;

          this.mobileGroups.forEach((sibling) => {
            if (sibling !== group) sibling.open = false;
          });
        },
        { signal }
      );
    });

    this.addEventListener(
      'click',
      (event) => {
        if (event.target instanceof Element && event.target.closest('a')) {
          this.closeDesktopMenus();
          this.closeMobileMenu();
        }
      },
      { signal }
    );

    /*
     * A click on anything that is not itself focusable — a <label>'s text, an
     * icon, a plain <span> — blurs whatever was focused (relatedTarget is
     * null, because nothing has taken focus yet) BEFORE the click event that
     * follows mouseup has a chance to run. The More rail's items are exactly
     * this: clicking one is what checks its radio and moves focus onto it,
     * but that only happens once the click resolves.
     *
     * A same-tick synthetic click (down and up with no gap) never surfaces
     * this — the whole cascade finishes before the next paint. A real click
     * always has SOME press duration, which is enough for the animation frame
     * below to run in between: it sees nothing focused yet, concludes focus
     * left the header, and closes the panel — before the label's own click
     * has checked anything. Measured: the rail's target radio was never
     * checked at all, because the panel had already gone `hidden` by the time
     * mouseup arrived.
     *
     * So a focusout caused by a pointer press in progress waits for that
     * press to resolve (pointerup) before judging where focus landed, rather
     * than judging mid-click. A focusout with no pointer involved — Tab
     * carrying focus past the last item — still closes immediately; nothing
     * about that path changes.
     */
    let pointerPressActive = false;

    this.addEventListener(
      'pointerdown',
      () => {
        pointerPressActive = true;
      },
      { signal }
    );

    const closeIfFocusLeft = () => {
      if (this.contains(document.activeElement)) return;

      this.closeDesktopMenus();
      this.closeMobileMenu();
    };

    this.addEventListener(
      'focusout',
      () => {
        if (pointerPressActive) {
          document.addEventListener(
            'pointerup',
            () => requestAnimationFrame(closeIfFocusLeft),
            { signal, once: true }
          );
          return;
        }

        requestAnimationFrame(closeIfFocusLeft);
      },
      { signal }
    );

    document.addEventListener(
      'pointerup',
      () => {
        pointerPressActive = false;
      },
      { signal }
    );

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!(event.target instanceof Node) || !this.contains(event.target)) {
          this.closeDesktopMenus();
          this.closeMobileMenu();
        }
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;

        if (this.mobileDrawer?.open) {
          this.closeMobileMenu();
          this.mobileTrigger?.focus();
          return;
        }

        if (this.activeMenuKey) {
          const trigger = this.activeMenuTrigger;
          this.closeDesktopMenus();
          trigger?.focus();
        }
      },
      { signal }
    );

    this.desktopMedia.addEventListener(
      'change',
      (event) => {
        if (event.matches) {
          this.closeMobileMenu();
        } else {
          this.closeDesktopMenus();
        }
      },
      { signal }
    );

    this.hoverMedia.addEventListener(
      'change',
      () => this.closeDesktopMenus(),
      { signal }
    );

    this.setUpSearchShortcut(signal);
    this.setUpMoreRail(signal);
    this.scheduleMenuImageWarm(signal);
  }

  /**
   * Pointing at a More rail item opens it.
   *
   * THIS IS AN ENHANCEMENT, NOT THE MECHANISM. The rail is a radio group and
   * assets/signal-menu.css shows the matching pane, so clicking and the arrow
   * keys already work with this file absent. All that is added here is hovering.
   *
   * The delay is what makes it usable. Dragging the pointer from a rail item
   * diagonally toward the products it just revealed crosses whatever sits below
   * it, and without a delay that repaints the pane out from under the hand
   * reaching for it. A normal-speed diagonal clears the neighbour well inside
   * 70ms, so the pane only changes for a pointer that actually stopped on
   * something — no geometry, no aim cone, and nothing to keep in sync with the
   * rail's dimensions.
   *
   * @param {AbortSignal} signal
   */
  setUpMoreRail(signal) {
    const items = this.querySelectorAll('[data-signal-rail-item]');
    if (items.length === 0) return;

    items.forEach((item) => {
      item.addEventListener(
        'pointerenter',
        (event) => {
          // Touch fires pointerenter on tap, which would fight the tap itself.
          if (event.pointerType === 'touch') return;
          if (!this.desktopMedia.matches || !this.hoverMedia.matches) return;

          this.cancelRailSwitch();

          const id = item.getAttribute('for');
          const radio = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
          if (!(radio instanceof HTMLInputElement) || radio.checked) return;

          this.railSwitchTimer = window.setTimeout(() => {
            this.railSwitchTimer = undefined;
            radio.checked = true;
          }, 70);
        },
        { signal }
      );

      item.addEventListener('pointerleave', () => this.cancelRailSwitch(), {
        signal,
      });
    });
  }

  cancelRailSwitch() {
    if (this.railSwitchTimer === undefined) return;

    window.clearTimeout(this.railSwitchTimer);
    this.railSwitchTimer = undefined;
  }

  /*
   * Closing the panel returns the rail to its resting pane, so More always
   * opens saying the same thing rather than resuming wherever the last visit
   * left off — which would quietly make one game the important one for the rest
   * of the session.
   */
  resetMoreRail() {
    const rest = this.querySelector('.signal-more-rail__radio');
    if (rest instanceof HTMLInputElement) rest.checked = true;
  }

  /**
   * Panels ship `hidden`, and a browser does not fetch images inside a
   * display:none subtree — so the first time a product panel opened, every
   * thumbnail began downloading at once and the grid filled in visibly.
   *
   * Flipping the originals to loading="eager" would not help, for the same
   * reason: they are still inside a hidden subtree. So the fetch has to happen
   * out of band. Copying srcset and sizes onto a detached Image makes the
   * browser run the same candidate selection it will run later, so the URL that
   * lands in the cache is the one the real element goes on to request.
   *
   * Document order is the fetch order, and that is good enough: Chrome works
   * the queue roughly in sequence, and the More rail's hover-only panes — 55 of
   * the 122 images (2026-09-14) — already come last in it. Splitting the warm
   * into two waves, visible-on-open first, was measured on a throttled 6 Mbps
   * line and changed nothing (2.05s to the visible set either way), so it was
   * not kept. The exposed window is the ~2s after a cold load, before idle.
   *
   * @param {AbortSignal} signal
   */
  scheduleMenuImageWarm(signal) {
    const warm = () => this.warmMenuImages();

    // Idle, so thumbnails never compete with the page's own first paint.
    if ('requestIdleCallback' in window) {
      const handle = requestIdleCallback(warm, { timeout: 4000 });
      this.cancelMenuImageWarm = () => cancelIdleCallback(handle);
    } else {
      const handle = window.setTimeout(warm, 2500);
      this.cancelMenuImageWarm = () => window.clearTimeout(handle);
    }

    // Reaching for the nav is a stronger signal than idle time and still lands
    // before the panel opens, so it covers a page that never goes idle.
    this.querySelector('.signal-header__desktop-nav')?.addEventListener(
      'pointerenter',
      warm,
      { signal, once: true }
    );
  }

  warmMenuImages() {
    if (this.menuImagesWarmed) return;
    // Below 1180px these panels never open, so warming them is pure waste.
    if (!this.desktopMedia.matches) return;

    const connection = /** @type {any} */ (navigator).connection;
    if (connection?.saveData) return;
    if (/(^|-)2g$/.test(connection?.effectiveType ?? '')) return;

    this.menuImagesWarmed = true;

    this.querySelectorAll('[data-signal-menu-panel] img').forEach((node) => {
      const img = /** @type {HTMLImageElement} */ (node);
      const src = img.getAttribute('src');
      if (!src) return;

      const preload = new Image();
      // Both must be set before src, or selection runs without them.
      const sizes = img.getAttribute('sizes');
      const srcset = img.getAttribute('srcset');
      if (sizes) preload.sizes = sizes;
      if (srcset) preload.srcset = srcset;
      preload.src = src;
      this.warmedImages.push(preload);
    });
  }

  /**
   * Ctrl+K — or Cmd+K on Apple platforms — opens the search dialog.
   *
   * The shortcut clicks the existing search button rather than reaching into the
   * dialog component, so focus handling, scroll locking and the close behaviour all
   * stay in one place instead of being reimplemented here.
   *
   * `preventDefault` is not optional: Ctrl+K is bound to the browser's own search bar
   * in Firefox, and Cmd+K to a location-bar search in Safari. Without it the page
   * shortcut and the browser shortcut both fire.
   *
   * @param {AbortSignal} signal
   */
  setUpSearchShortcut(signal) {
    const trigger = /** @type {HTMLElement | null} */ (this.querySelector('search-button button'));
    if (!trigger) return;

    // navigator.platform is deprecated but still the only thing Safari reports; the
    // modern field is tried first and this falls back to it.
    const platform =
      /** @type {any} */ (navigator).userAgentData?.platform || navigator.platform || '';
    const isApple = /mac|iphone|ipad|ipod/i.test(platform);

    const modifierKey = this.querySelector('[data-signal-search-hint-modifier]');
    if (modifierKey && isApple) modifierKey.textContent = '⌘';

    // The button is only the magnifier's width, so without this the shortcut label
    // and the padding around it look like part of the control but do nothing when
    // clicked. Forwarding from the wrapper makes the whole pill the hit area while
    // leaving the real button as the thing that is focused and labelled.
    const field = trigger.closest('.signal-header__search-field');
    if (field) {
      field.addEventListener(
        'click',
        (event) => {
          if (event.target instanceof Node && trigger.contains(event.target)) return;
          trigger.click();
        },
        { signal }
      );
    }

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'k' && event.key !== 'K') return;
        if (isApple ? !event.metaKey : !event.ctrlKey) return;
        if (event.altKey || event.shiftKey) return;

        // Already open — let the dialog keep the keystroke rather than reopening it.
        if (document.querySelector('#search-modal dialog[open]')) return;

        event.preventDefault();
        trigger.click();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.cancelDesktopMenuOpen();
    this.cancelDesktopMenuClose();
    this.cancelRailSwitch();
    this.cancelMenuImageWarm?.();

    // Belt and suspenders: a theme-editor re-render can disconnect this
    // element while the drawer is still open, and the lock should not
    // outlive it.
    if (this.mobileDrawer) unlockScroll(this.mobileDrawer);

    if (this.pointerFrame !== undefined) {
      cancelAnimationFrame(this.pointerFrame);
      this.pointerFrame = undefined;
    }

    delete this.dataset.initialized;
  }

  /**
   * @param {string} key
   * @param {HTMLElement} trigger
   */
  toggleDesktopMenu(key, trigger) {
    const panel = this.panelsByKey.get(key);
    if (!panel) return;

    if (this.activeMenuKey === key && !panel.hidden) {
      this.closeDesktopMenus();
    } else {
      this.openDesktopMenu(key, trigger);
    }
  }

  /**
   * @param {string} key
   * @param {HTMLElement} trigger
   */
  openDesktopMenu(key, trigger) {
    const panel = this.panelsByKey.get(key);
    if (!panel) return;

    this.cancelDesktopMenuOpen();
    this.cancelDesktopMenuClose();
    this.closeMobileMenu();

    this.menuPanels.forEach((menuPanel) => {
      menuPanel.hidden = menuPanel !== panel;
    });

    this.menuTriggers.forEach((menuTrigger) => {
      menuTrigger.setAttribute(
        'aria-expanded',
        String(menuTrigger === trigger)
      );
    });

    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    this.activeMenuKey = key;
    this.activeMenuTrigger = trigger;
  }

  /*
   * True when the pointer is over the active trigger's column, over the open
   * panel, or in the corridor between them. The corridor spans the full width
   * both boxes cover, so a diagonal move toward a panel wider than the trigger
   * still counts.
   */
  /**
   * @param {number} x
   * @param {number} y
   */
  isPointerInMenuRegion(x, y) {
    const panel =
      this.activeMenuKey === undefined
        ? undefined
        : this.panelsByKey.get(this.activeMenuKey);
    const trigger = this.activeMenuTrigger;
    if (!panel || panel.hidden || !trigger || !this.bar) return false;

    const bar = this.bar.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const menu = panel.getBoundingClientRect();

    // As tall as the bar (the link is shorter than the bar), but as wide as
    // the trigger itself — not the whole header — so moving off it sideways
    // still counts as leaving.
    const anchor = {
      left: triggerRect.left,
      right: triggerRect.right,
      top: bar.top,
      bottom: bar.bottom,
    };

    /** @param {{ left: number, right: number, top: number, bottom: number }} rect */
    const within = (rect) =>
      x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

    if (within(anchor) || within(menu)) return true;

    return (
      y >= Math.min(anchor.bottom, menu.top) &&
      y <= Math.max(anchor.bottom, menu.top) &&
      x >= Math.min(anchor.left, menu.left) &&
      x <= Math.max(anchor.right, menu.right)
    );
  }

  /**
   * @param {string} key
   * @param {HTMLElement} trigger
   */
  scheduleDesktopMenuOpen(key, trigger) {
    this.cancelDesktopMenuOpen();

    // Moving between triggers switches instantly. The delay below buys a
    // decision about whether the pointer meant to be on the nav at all, and
    // with a panel already open that decision has been made — charging for it
    // again on the way from Magic to One Piece reads as the menu lagging, not
    // as it being careful.
    if (this.activeMenuKey !== undefined) {
      this.openDesktopMenu(key, trigger);
      return;
    }

    // Short enough that a hover aimed at this link still opens on contact, long
    // enough that a pointer crossing the bar toward the cart passes over three
    // triggers without strobing a panel out of each one.
    this.desktopMenuOpenTimer = window.setTimeout(
      () => this.openDesktopMenu(key, trigger),
      140
    );
  }

  cancelDesktopMenuOpen() {
    if (this.desktopMenuOpenTimer === undefined) return;

    window.clearTimeout(this.desktopMenuOpenTimer);
    this.desktopMenuOpenTimer = undefined;
  }

  scheduleDesktopMenuClose() {
    if (!this.desktopMedia.matches || !this.hoverMedia.matches) return;

    this.cancelDesktopMenuClose();
    // Long enough to cross the header/panel gap at an unhurried pace, and to
    // hesitate mid-move, without the menu feeling like it lingers.
    this.desktopMenuCloseTimer = window.setTimeout(
      () => this.closeDesktopMenus(),
      260
    );
  }

  cancelDesktopMenuClose() {
    if (this.desktopMenuCloseTimer === undefined) return;

    window.clearTimeout(this.desktopMenuCloseTimer);
    this.desktopMenuCloseTimer = undefined;
  }

  closeDesktopMenus() {
    this.cancelDesktopMenuOpen();
    this.cancelDesktopMenuClose();
    this.cancelRailSwitch();
    this.resetMoreRail();

    this.menuPanels.forEach((panel) => {
      panel.hidden = true;
    });
    this.menuTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    this.activeMenuKey = undefined;
    this.activeMenuTrigger = undefined;
  }

  toggleColorMode() {
    const next =
      document.documentElement.dataset.signalMode === 'dark' ? 'light' : 'dark';

    document.documentElement.dataset.signalMode = next;

    try {
      localStorage.setItem('signal-mode', next);
    } catch (error) {
      // Storage is unavailable in a partitioned iframe or with cookies blocked.
      // The mode still applies for this page view.
    }

    this.syncColorMode();
  }

  // Called on connect as well as on toggle: the <html> attribute outlives a
  // theme-editor section re-render, but this button's DOM does not.
  syncColorMode() {
    const isDark = document.documentElement.dataset.signalMode === 'dark';
    const themeColor = isDark ? '#15141a' : '#f2f0f5';

    this.modeToggle?.setAttribute('aria-pressed', String(isDark));
    this.headerComponent?.setAttribute('data-theme-color', themeColor);
    // Keeps the browser chrome (address bar on mobile) in step immediately —
    // without this it held the previous mode's colour until the sticky
    // threshold's own scroll-driven update happened to run.
    changeMetaThemeColor(themeColor);
  }

  /*
   * The <summary> already flipped `open` on its <details> natively before this
   * runs — this only keeps everything downstream of that in sync: aria-expanded
   * on the trigger, the desktop menus, the accordions closing behind it, and the
   * scroll lock Horizon's own drawers already use (assets/utilities.js). Also
   * runs for a programmatic close (closeMobileMenu below sets `.open = false`,
   * which fires this same 'toggle' event), so there is one place this state is
   * decided rather than two that can drift.
   */
  syncMobileDrawer() {
    if (!this.mobileDrawer || !this.mobileTrigger) return;

    const isOpen = this.mobileDrawer.open;
    this.mobileTrigger.setAttribute('aria-expanded', String(isOpen));

    if (isOpen) {
      this.closeDesktopMenus();
      lockScroll(this.mobileDrawer);
    } else {
      this.mobileGroups.forEach((group) => {
        group.open = false;
      });
      unlockScroll(this.mobileDrawer);
    }
  }

  closeMobileMenu() {
    if (!this.mobileDrawer?.open) return;

    // Fires this element's own 'toggle' listener (syncMobileDrawer above),
    // which is what actually collapses the accordions and releases the lock.
    this.mobileDrawer.open = false;
  }
}

if (!customElements.get('signal-header')) {
  customElements.define('signal-header', SignalHeader);
}
